import { useState } from 'react';
import { C } from '@ridgewill/brand';

/**
 * The handful of form pieces every screen here needs.
 *
 * Extracted after the second screen repeated them, not before — three inline
 * copies of a label-and-input is when the abstraction is earned rather than
 * guessed at.
 */

/**
 * A labelled row. `id` is OPTIONAL, and leaving it off is not laziness.
 *
 * A `<label htmlFor>` forwards its clicks to the control it names, and the label
 * is a full-width block. Point one at a BUTTON and the entire width of the row,
 * including the empty space beside the word, becomes a second way to press that
 * button - which is what happened to the username row: clicking anywhere along
 * "Username" opened the editor, yards away from the Edit control.
 *
 * So a row whose content is not a single form control passes no `id` and gets a
 * plain caption instead. Nothing is lost: a label that names a button is not
 * describing the button anyway, and the button carries its own `aria-label`.
 */
export function Field({
  id,
  label,
  children,
}: {
  id?: string;
  label: string;
  children: React.ReactNode;
}) {
  const caption = { display: 'block', marginBottom: '.35rem' } as const;
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      {id ? (
        <label htmlFor={id} style={caption}>
          {label}
        </label>
      ) : (
        <span style={caption}>{label}</span>
      )}
      {children}
    </div>
  );
}

/**
 * `role="alert"` so a screen reader announces a failure that appears after a
 * submit. Without it the message renders silently and the only feedback is a
 * button that stopped spinning.
 */
export function Problem({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" style={{ margin: '.75rem 0', color: C.dangerText, fontSize: '.9rem' }}>
      {children}
    </p>
  );
}

export function Note({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'ok' | 'bad';
}) {
  /**
   * TOKENS, NOT LITERALS, and this was the one that mattered most (account#87).
   *
   * These were `#1b7f3b` and `#b3261e`, both picked against white. Every `C.*`
   * token is `var(--rw-x, <light>)`, so a literal here simply does not
   * participate in the theme: on a dark ground it stays a dark green or a dark
   * red on near-black and becomes the least readable text on the page.
   *
   * That is not hypothetical for these two. `ok` and `bad` are what render
   * "Active" and "Payment failed" on the subscriptions section, so the worst
   * contrast in dark would have landed on the screen about somebody's money.
   */
  const color = tone === 'ok' ? C.successText : tone === 'bad' ? C.dangerText : C.textMid;
  return <p style={{ margin: '.35rem 0 0', color, fontSize: '.85rem', lineHeight: 1.45 }}>{children}</p>;
}

/**
 * "THIS IS NOT A CREDENTIAL FIELD" - spread onto profile inputs.
 *
 * There is no standard for this. Every password manager invented its own
 * attribute, `autocomplete="off"` alone is widely ignored, and the failure is
 * not cosmetic: LastPass filled the account portal's Username box with the
 * saved credential for the site, which is an EMAIL. The stored username was
 * correct in the database the whole time and the screen showed an email over
 * the top of it, so it was reported as "my username will not save".
 *
 * | Manager | Attribute | Note |
 * |---|---|---|
 * | LastPass | `data-lpignore="true"` | needs the explicit value, presence alone is not enough |
 * | 1Password | `data-1p-ignore` | boolean; documented to work best alongside `autocomplete="off"` |
 * | Bitwarden | `data-bwignore` | boolean |
 * | Dashlane | `data-form-type="other"` | |
 *
 * This is a HINT, not a control. A manager is free to ignore all of it, which
 * is why the username field also renders as text until you press Edit - see
 * screens/Profile. Belt and braces, because the belt is somebody else's code.
 */
export const notACredentialField = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
};

export const input: React.CSSProperties = {
  width: '100%',
  padding: '.6rem .75rem',
  fontSize: '1rem',
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 4,
};

export const button: React.CSSProperties = {
  marginTop: '.5rem',
  padding: '.6rem 1rem',
  fontSize: '1rem',
  background: 'transparent',
  color: C.brand,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 4,
  cursor: 'pointer',
};

/**
 * A password field with a show/hide toggle.
 *
 * ── WHY THE TOGGLE IS WORTH THE CODE ────────────────────────────────
 *
 * Typing a long password blind, three times, into three adjacent boxes is how
 * "those two passwords do not match" happens — and the person cannot see which
 * of the two is wrong, so the fix is to retype both. Password managers make it
 * worse rather than better: they fill the first field and leave the reader with
 * no way to confirm what landed there.
 *
 * ── IT IS A `button`, NOT AN ICON ON THE INPUT ──────────────────────
 *
 * A real button so it is reachable by keyboard and announced. `aria-pressed`
 * carries the state, and the label says what the NEXT press does — "Show
 * password" while hidden — which is the convention screen-reader users expect
 * and the opposite of labelling it with the current state.
 *
 * `tabIndex={-1}` is deliberately NOT set. Skipping it in the tab order would
 * make the control invisible to exactly the users who most need a labelled
 * button rather than a glyph.
 */
export function PasswordField({
  id,
  label,
  value,
  autoComplete,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
}) {
  const [shown, setShown] = useState(false);

  return (
    <Field id={id} label={label}>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // Room for the button, so a long value does not run underneath it.
          style={{ ...input, paddingRight: '2.75rem' }}
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '2.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: C.textMid,
          }}
        >
          <Eye open={shown} />
        </button>
      </div>
    </Field>
  );
}

/** Inline SVG for the same reason the rest of this app avoids an icon package:
 *  two paths do not justify a dependency. `aria-hidden` because the button that
 *  wraps it already carries the label. */
function Eye({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {/* The struck-through variant means "hidden", so the slash appears when
          the text IS shown — pressing it hides. Getting this backwards is the
          usual bug and reads as the control doing nothing. */}
      {open && <path d="M3 3l18 18" />}
    </svg>
  );
}
